// Gift receipt for a paid bill: what was bought, by size, with no prices, plus
// the bill number staff search in Orders to return or exchange it. It is built
// here as its own small page instead of going through the till's receipt
// template, which always prints amounts (and on hardware-controller tills has no
// way to hide them).

export const escapeHtml = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const fmtQty = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(2));

// The lines a gift receipt lists: sold, not cancelled, same product merged.
export function giftReceiptLines(order) {
  const merged = new Map();
  (order?.items || []).forEach((item) => {
    const qty = Number(item?.weight ?? item?.qty);
    if (!(qty > 0) || item?.isCanceled === "Y") return;
    const key = item.productId || item.value || item.name;
    const prev = merged.get(key);
    if (prev) prev.qty += qty;
    else merged.set(key, { name: item.name || "Item", code: item.value || "", qty });
  });
  return [...merged.values()];
}

export function buildGiftReceiptHtml(order, tillData) {
  const store = tillData?.tillAccess?.csBunit?.name || "";
  const lines = giftReceiptLines(order);
  const rows = lines
    .map((l) => `<tr><td>${escapeHtml(l.name)}${l.code ? `<div class="code">${escapeHtml(l.code)}</div>` : ""}</td><td class="qty">${fmtQty(l.qty)}</td></tr>`)
    .join("");
  const date = String(order?.orderTime || order?.orderDate || "").split(" ")[0];
  return `<!doctype html><html><head><meta charset="utf-8"><title>Gift receipt</title><style>
body{font-family:Arial,Helvetica,sans-serif;width:72mm;margin:0 auto;padding:4mm 0;color:#000;font-size:12px}
h1{font-size:16px;text-align:center;margin:0 0 2mm}
.store{text-align:center;margin:0 0 3mm}
table{width:100%;border-collapse:collapse}
th,td{text-align:left;padding:1mm 0;vertical-align:top;border-bottom:1px dashed #000}
.qty{text-align:right;width:12mm}
.code{font-size:10px}
.bill{text-align:center;font-size:14px;font-weight:bold;margin:4mm 0 1mm;letter-spacing:1px}
.note{text-align:center;margin:0}
</style></head><body>
<h1>GIFT RECEIPT</h1>
${store ? `<p class="store">${escapeHtml(store)}</p>` : ""}
<table><thead><tr><th>Item</th><th class="qty">Qty</th></tr></thead><tbody>${rows}</tbody></table>
<p class="bill">${escapeHtml(order?.documentno || "")}</p>
${date ? `<p class="note">${escapeHtml(date)}</p>` : ""}
<p class="note">Bring this receipt to exchange or return.</p>
</body></html>`;
}

// CW Browser: straight to the till's default printer. Web: the browser's print
// dialog from a hidden frame. Resolves { ok } or { ok: false, message }.
export async function printGiftReceipt(html, what = "gift receipt") {
  const hw = typeof window !== "undefined" ? window.electronAPI?.hardware : null;
  if (hw?.printDirect) {
    try {
      const printer = await hw.getDefaultPrinter?.();
      if (!printer) return { ok: false, message: "No receipt printer is set on this till. Pick one in printer settings." };
      const result = await hw.printDirect(printer, html, { silent: true });
      return result?.success === false ? { ok: false, message: `The ${what} didn't print. Check the printer and try again.` } : { ok: true };
    } catch {
      return { ok: false, message: `The ${what} didn't print. Check the printer and try again.` };
    }
  }
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.style.cssText = "position:fixed;width:0;height:0;border:0;right:0;bottom:0";
  document.body.appendChild(frame);
  try {
    frame.contentDocument.open();
    frame.contentDocument.write(html);
    frame.contentDocument.close();
    frame.contentWindow.focus();
    frame.contentWindow.print();
    return { ok: true };
  } catch {
    return { ok: false, message: `The ${what} didn't print. Check the printer and try again.` };
  } finally {
    setTimeout(() => frame.remove(), 1000);
  }
}
